import chart from "@assets/redesign/icons/chart.svg";
import origDrop from "@assets/redesign/icons/drop.svg";
import wlpDrop from "@components/WLP202603/assets/redesign/icons/drop.svg";
import useChartLiveDataSource, { CleanChartValueConverter } from "@components/Charts/ChartLiveDataSource";
import CleanChart from "@components/Charts/CleanChart";
import { parseLocationId } from "@components/Charts/CleanChart.utils";
import useChartPeriodControl from "@components/Charts/CleanChartPeriod.control";
import { LiveValueListener } from "@components/Context/SocketContext";
import Loader from "@components/global/Loader/Loader";
import Icon from "@components/global/new/Icon/Icon";
import { IStatisticControl3 } from "@mobx/hooks/types";
import mainLocationsStore from "@mobx/store/locations";
import { ISpeedMessage } from "@services/http/socket/responses";
import { getDevivedHash } from "@utils/hash.utils";
import { Collapse } from "antd";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import moment from "moment";
import { useEffect, useState } from "react";
import { VISIBILITY_TIMEOUT_MS, WLP202603 } from "../../../config";
import styles from "./LocationChart.module.scss";

const drop = WLP202603 ? wlpDrop : origDrop;

export interface ILocationChart3Props {
  readonly title?: string;
  control: IStatisticControl3;
}

const KEY_TIME = "loc-stat-refresh";

function updateLastTime3(id: any) {
  localStorage.setItem(`${KEY_TIME}-${id}`, `${Date.now()}`);
}

function isTimeElapsed3(id: any): boolean {
  const lastTime = localStorage.getItem(`${KEY_TIME}__${id}`);
  if (lastTime !== null) {
    const diff = Date.now() - parseInt(lastTime);
    if (diff > 5 * 60 * 1000) {
      return true;
    }
  }
  return false;
}

interface ILocationChartValue3 {
  speed: number;
}

const useControl = (props: ILocationChart3Props) => {
  const { control: statControl } = props;
  const locationStore = mainLocationsStore.activeLocation;
  const [open, setOpen] = useState(false);

  const periodControl = useChartPeriodControl({
    onPeriodChanged: (p) => statControl.update(p, "onPeriodChanged")
  });

  const converter: CleanChartValueConverter<ILocationChartValue3> = (timeInSec, value) => {
    return [{
      datasetIndex: 0,
      timeInSec,
      value: value.speed,
      state: -1,
    }]
  }

  const liveDataSource = useChartLiveDataSource<ILocationChartValue3>(converter);

  const onOpenHandler = () => setOpen((prev) => !prev);

  const onLocationSpeed: LiveValueListener = (message) => {
    const data = message.event as ISpeedMessage;
    if (parseLocationId(message.event.id) === locationStore?.location.Id) {
      liveDataSource.enqueue(moment().unix(), { speed: data.speed });
    }
  }

  useEffect(() => {
    statControl.load(onLocationSpeed);
    if (isTimeElapsed3(statControl.getId())) {
      refresh("isTimeElapsed");
    }
    return () => {
      statControl.unload(onLocationSpeed);
    }
  }, []);

  function refresh(tag: string) {
    updateLastTime3(statControl.getId());
    statControl.update(periodControl.period, `refresh + ${tag}`);
  }

  return {
    dataset: statControl.datasets,
    isLoading: statControl.isLoading,
    statistic: statControl.statistic,
    liveDataSource,
    open,
    title: props.title || locationStore?.location.Name,
    periodControl,
    onOpenToggle: onOpenHandler,
    refresh,
  };
};


const LocationChart3 = observer((props: ILocationChart3Props) => {
  const ctrl = useControl(props);
  const [active, setActive] = useState("");

  return (
    <Collapse
      className={styles.collapse}
      collapsible="icon"
      ghost
      activeKey={active}
      items={[
        {
          key: "1",
          label: <div className={styles.switcher_with_stat}>
            <h4 className={styles.title}>
              {props.title}
            </h4>
            <ul>
              <li>
                <strong>MIN:</strong> {getDevivedHash(ctrl.statistic[0])}
                {ctrl.isLoading && <Loader size={16} />}
              </li>
              <li>
                <strong>MAX:</strong> {getDevivedHash(ctrl.statistic[1])}
                {ctrl.isLoading && <Loader size={16} />}
              </li>
              <li>
                <strong>AVG:</strong> {getDevivedHash(ctrl.statistic[2])}
                {ctrl.isLoading && <Loader size={16} />}
              </li>
            </ul>
            <Icon
              className={styles.chartIcon}
              src={chart}
              noHover
            />
            <Icon
              className={cn(styles.collapseButton, {[styles.collapsed]: !active})}
              src={drop}
              onClick={() => {
                setActive(active ? "" : "1");
                ctrl.onOpenToggle();
              }}
            />
          </div>,
          children: <CleanChart
            showLegend={false}
            showY1Scales={true}
            periodControl={ctrl.periodControl}
            datasets={ctrl.dataset}
            liveDataSource={ctrl.liveDataSource}
            isLoading={ctrl.isLoading}
            visibilityTimeoutMs={VISIBILITY_TIMEOUT_MS}
            onVisible={() => ctrl.refresh("onVisible")}
            className={styles.chart}
          />,
          showArrow: false,
        },
      ]}
    />
  );
});

LocationChart3.displayName = "LocationChart3";

export default LocationChart3;
